<script setup>
import { router } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import BankAccountFormModal from '@/components/admin/BankAccountFormModal.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    bankAccounts: Object,
    companies: Array,
    currencies: Array,
});

const bankAccountsData = computed(() => {
    return props.bankAccounts.data || [];
});

const loading = ref(false);

// Modal state
const showModal = ref(false);
const selectedBankAccount = ref(null);

const openCreateModal = () => {
    selectedBankAccount.value = null;
    showModal.value = true;
};

const openEditModal = (bankAccount) => {
    selectedBankAccount.value = { ...bankAccount };
    showModal.value = true;
};

const onModalSaved = () => {
    router.reload({ only: ['bankAccounts'] });
};

const destroy = (bankAccount) => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} ${bankAccount.name}?`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('bank-accounts.destroy', bankAccount.id), {
                preserveScroll: true,
                preserveState: true,
                onStart: () => showLoading('Deleting bank account...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: 'Success', detail: 'Bank account deleted successfully', life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete bank account', life: 3000 });
                }
            });
        }
    });
};

const onPage = (event) => {
    const page = event.page + 1;
    router.get(route('bank-accounts.index'), { page }, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading('Loading bank accounts...'),
        onFinish: () => hideLoading()
    });
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};

const getStatusSeverity = (isActive) => {
    return isActive ? 'success' : 'danger';
};

const getTypeSeverity = (type) => {
    const severities = {
        'BANK': 'info',
        'PAYPAL': 'warning',
        'WISE': 'success',
        'CRYPTO': 'help'
    };
    return severities[type] || 'secondary';
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />

        <!-- Bank Account Form Modal -->
        <BankAccountFormModal
            v-model="showModal"
            :bankAccount="selectedBankAccount"
            :companies="companies"
            :currencies="currencies"
            @saved="onModalSaved"
        />

        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Bank Accounts') }}</h2>
            <Button :label="$t('Add Bank Account')" icon="pi pi-plus" class="p-button-success" @click="openCreateModal" />
        </div>

        <DataTable
            :value="bankAccountsData"
            :paginator="true"
            :rows="15"
            :totalRecords="bankAccounts.total"
            :lazy="true"
            @page="onPage"
            :loading="loading"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="p-datatable-sm">

            <template #empty>
                <div class="text-center py-4">{{ $t('No bank accounts found.') }}</div>
            </template>

            <Column field="name" :header="$t('Name')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="text-blue-600 hover:text-blue-800 font-medium cursor-pointer" @click="openEditModal(slotProps.data)">
                        {{ slotProps.data.name }}
                    </span>
                </template>
            </Column>

            <Column field="accepted_currencies" :header="$t('Currencies')" style="min-width: 130px">
                <template #body="slotProps">
                    <div class="flex items-center gap-1">
                        <template v-if="slotProps.data.accepted_currencies?.length">
                            <Tag :value="slotProps.data.accepted_currencies[0]" severity="secondary" />
                            <span
                                v-if="slotProps.data.accepted_currencies.length > 1"
                                class="text-xs font-semibold text-surface-400 cursor-default"
                                v-tooltip.top="slotProps.data.accepted_currencies.join(', ')"
                            >+{{ slotProps.data.accepted_currencies.length - 1 }}</span>
                        </template>
                        <span v-else>-</span>
                    </div>
                </template>
            </Column>

            <Column field="type" :header="$t('Type')" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.type" :severity="getTypeSeverity(slotProps.data.type)" />
                </template>
            </Column>

            <Column field="company" :header="$t('Company')" style="min-width: 150px">
                <template #body="slotProps">
                    {{ slotProps.data.company?.name || '-' }}
                </template>
            </Column>

            <Column field="bank_name" :header="$t('Bank/Details')" style="min-width: 150px">
                <template #body="slotProps">
                    <div v-if="slotProps.data.type === 'BANK' || slotProps.data.type === 'WISE'">
                        {{ slotProps.data.bank_name || '-' }}
                    </div>
                    <div v-else-if="slotProps.data.type === 'PAYPAL'">
                        {{ slotProps.data.paypal_email || '-' }}
                    </div>
                    <div v-else-if="slotProps.data.type === 'CRYPTO'">
                        {{ slotProps.data.crypto_type || '-' }}
                    </div>
                    <div v-else>
                        -
                    </div>
                </template>
            </Column>

            <Column field="is_default" :header="$t('Default')" style="min-width: 100px">
                <template #body="slotProps">
                    <i v-if="slotProps.data.is_default" class="pi pi-check-circle text-green-500" v-tooltip.top="$t('Default account')"></i>
                    <i v-else class="pi pi-circle text-gray-300"></i>
                </template>
            </Column>

            <Column field="is_active" :header="$t('Status')" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag
                        :value="slotProps.data.is_active ? $t('Active') : $t('Inactive')"
                        :severity="getStatusSeverity(slotProps.data.is_active)"
                    />
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="flex gap-2">
                        <Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-info"
                            @click="openEditModal(slotProps.data)" v-tooltip.top="$t('Edit')" />
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger"
                            @click="destroy(slotProps.data)" v-tooltip.top="$t('Delete')" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
